import { useState } from 'react';

import type { CompanyCreateBody, CompanyOut } from '@solar/api-client';
import { ApiError } from '@solar/api-client';
import { ErrorBox, Field, Spinner, formatPhone, useAsync } from '@solar/ui';

import { api } from '../api';
import { Modal } from '../Modal';

/**
 * 설치 회사 정보 — 레거시 WebService/company 파리티.
 *
 * 업체 관리자에게는 서버가 자기 회사만 내려준다(회사 격리). UI 는 응답을
 * 그대로 그리고, 권한 오류(403)도 서버 메시지를 그대로 보여준다.
 */

const SIZE = 30;

export function CompanyManage() {
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const list = useAsync(
    () => api.listCompanies({ name: applied, page, size: SIZE }),
    [applied, page],
  );

  const total = list.data?.total ?? 0;
  const lastPage = Math.max(1, Math.ceil(total / SIZE));
  const row = list.data?.items.find((c) => c.id === selected) ?? null;

  return (
    <div className="admin__body">
      <section className="admin__list">
        <div className="toolbar">
          <form
            className="search"
            onSubmit={(e) => {
              e.preventDefault();
              setApplied(keyword.trim());
              setPage(1);
            }}
          >
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="회사명"
            />
            <button type="submit">검색</button>
          </form>
          <button type="button" className="primary create" onClick={() => setCreateOpen(true)}>
            등록
          </button>
        </div>

        {list.loading && <Spinner />}
        {list.error && <ErrorBox message={list.error} onRetry={list.reload} />}

        {list.data && (
          <table className="grid">
            <thead>
              <tr>
                <th>회사명</th>
                <th>대표명</th>
                <th>연락처</th>
                <th>도로명 주소</th>
              </tr>
            </thead>
            <tbody>
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={4} className="muted">
                    등록된 회사가 없습니다.
                  </td>
                </tr>
              )}
              {list.data.items.map((c) => (
                <tr
                  key={c.id}
                  className={c.id === selected ? 'selected' : ''}
                  onClick={() => setSelected(c.id)}
                >
                  <td>{c.name}</td>
                  <td>{c.ceo ?? '-'}</td>
                  <td>{formatPhone(c.tel)}</td>
                  <td className="addr">{c.addr ?? '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="pager">
          <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
            이전
          </button>
          <span>
            {page} / {lastPage} (총 {total}건)
          </span>
          <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= lastPage}>
            다음
          </button>
        </div>
      </section>

      <aside className="admin__aside">
        {row === null ? (
          <p className="muted">회사를 선택하세요.</p>
        ) : (
          <CompanyEdit
            key={row.id}
            row={row}
            onClose={() => setSelected(null)}
            onChanged={() => list.reload()}
            onDeleted={() => {
              setSelected(null);
              list.reload();
            }}
          />
        )}
      </aside>

      {createOpen && (
        <CompanyCreateModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            list.reload();
          }}
        />
      )}
    </div>
  );
}

/** 등록/수정 공용 입력 묶음 — 레거시 detail.blade.php 의 필드 구성 그대로. */
interface CompanyForm {
  name: string;
  ceo: string;
  biz_no: string;
  tel: string;
  addr: string;
  old_addr: string;
  email: string;
  bank_name: string;
  account_number: string;
  account_name: string;
  etc: string;
}

function CompanyFields({
  form,
  onChange,
}: {
  form: CompanyForm;
  onChange: (patch: Partial<CompanyForm>) => void;
}) {
  const f = (key: keyof CompanyForm, label: string, required = false) => (
    <label className="f wide">
      {label}
      {required ? ' *' : ''}
      <input value={form[key]} onChange={(e) => onChange({ [key]: e.target.value })} />
    </label>
  );

  return (
    <>
      {f('name', '회사명', true)}
      {f('ceo', '대표명')}
      {f('biz_no', '사업자등록번호')}
      {f('tel', '연락처', true)}
      {f('addr', '신주소', true)}
      {f('old_addr', '구주소', true)}
      {f('email', '이메일주소')}
      {f('bank_name', '은행명')}
      {f('account_number', '계좌번호')}
      {f('account_name', '예금자명')}
      {f('etc', '기타사항')}
    </>
  );
}

function formToBody(form: CompanyForm): CompanyCreateBody {
  return {
    name: form.name.trim(),
    tel: form.tel.trim(),
    addr: form.addr.trim(),
    old_addr: form.old_addr.trim(),
    ceo: form.ceo.trim() || undefined,
    biz_no: form.biz_no.trim() || undefined,
    email: form.email.trim() || undefined,
    bank_name: form.bank_name.trim() || undefined,
    account_number: form.account_number.trim() || undefined,
    account_name: form.account_name.trim() || undefined,
    etc: form.etc.trim() || undefined,
  };
}

function validate(form: CompanyForm): string | null {
  if (!form.name.trim()) return '회사명을 입력해 주세요.';
  if (!form.tel.trim()) return '연락처를 입력해 주세요.';
  if (!form.addr.trim()) return '신주소를 입력해 주세요.';
  if (!form.old_addr.trim()) return '구주소를 입력해 주세요.';
  return null;
}

function CompanyEdit({
  row,
  onClose,
  onChanged,
  onDeleted,
}: {
  row: CompanyOut;
  onClose: () => void;
  onChanged: () => void;
  onDeleted: () => void;
}) {
  const [form, setForm] = useState<CompanyForm>({
    name: row.name,
    ceo: row.ceo ?? '',
    biz_no: row.biz_no ?? '',
    tel: row.tel ?? '',
    addr: row.addr ?? '',
    old_addr: row.old_addr ?? '',
    email: row.email ?? '',
    bank_name: row.bank_name ?? '',
    account_number: row.account_number ?? '',
    account_name: row.account_name ?? '',
    etc: row.etc ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  async function save() {
    const invalid = validate(form);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    setOk(null);
    try {
      await api.updateCompany(row.id, formToBody(form));
      setOk('저장했습니다.');
      onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '저장하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    setError(null);
    try {
      await api.deleteCompany(row.id);
      onDeleted();
    } catch (err) {
      // 진행 중 주문이 있는 회사 등 삭제 가드 409 메시지를 그대로 보여준다.
      setError(err instanceof ApiError ? err.message : '삭제하지 못했습니다.');
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="detail">
      <div className="detail__head">
        <h2>{row.name}</h2>
        <button type="button" className="link" onClick={onClose}>
          닫기
        </button>
      </div>

      <section className="panel">
        <Field label="상태">{row.is_active ? '운영중' : '중지'}</Field>
      </section>

      <h3 className="section">회사 정보</h3>
      <section className="panel form-grid">
        <CompanyFields form={form} onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))} />
      </section>

      {error && <p className="error">{error}</p>}
      {ok && <p className="ok">{ok}</p>}

      <div className="row form-actions">
        <button type="button" disabled={busy} onClick={() => setConfirmDelete(true)}>
          삭제
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void save()}>
          {busy ? '저장 중…' : '저장'}
        </button>
      </div>

      {confirmDelete && (
        <Modal title="회사 삭제" onClose={() => setConfirmDelete(false)}>
          <p>
            <strong>{row.name}</strong> 회사를 삭제합니다. 계속할까요?
          </p>
          <div className="row">
            <button type="button" onClick={() => setConfirmDelete(false)}>
              취소
            </button>
            <button type="button" className="primary" disabled={busy} onClick={() => void remove()}>
              삭제
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function CompanyCreateModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [form, setForm] = useState<CompanyForm>({
    name: '',
    ceo: '',
    biz_no: '',
    tel: '',
    addr: '',
    old_addr: '',
    email: '',
    bank_name: '',
    account_number: '',
    account_name: '',
    etc: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const invalid = validate(form);
    if (invalid !== null) {
      setError(invalid);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.createCompany(formToBody(form));
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '등록하지 못했습니다.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="설치 회사 등록" onClose={onClose}>
      <div className="form-grid">
        <CompanyFields form={form} onChange={(patch) => setForm((prev) => ({ ...prev, ...patch }))} />
      </div>
      {error && <p className="error">{error}</p>}
      <div className="row">
        <button type="button" onClick={onClose}>
          취소
        </button>
        <button type="button" className="primary" disabled={busy} onClick={() => void submit()}>
          {busy ? '등록 중…' : '등록'}
        </button>
      </div>
    </Modal>
  );
}
